import React, { useState, useEffect } from 'react';
import './DailyChallenge.css';

/**
 * Daily Challenge - One difficult question per day
 * Tracks daily streaks and completion
 */
const DailyChallenge = ({ data }) => {
  const [challenge, setChallenge] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [dailyStats, setDailyStats] = useState(null);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    loadDailyChallenge();
    loadDailyStats();
  }, [data]);

  const getTodayString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
  };

  const loadDailyStats = () => {
    const stats = JSON.parse(localStorage.getItem('dailyChallengeStats') || '{}');
    setDailyStats(stats);
    
    // Check if already completed today
    const today = getTodayString();
    if (stats[today]) {
      setHasCompleted(true);
    }
  };

  const loadDailyChallenge = () => {
    if (!data || !data.subjects) return;

    // Get all questions
    const allQuestions = data.subjects.flatMap(subject =>
      subject.topics.flatMap(topic =>
        topic.multiple_choice_questions.map(q => ({
          ...q,
          subject: subject.name,
          topic: topic.name
        }))
      )
    );

    if (allQuestions.length === 0) return;

    // Use today's date as seed for consistent daily question
    const today = getTodayString();
    const seed = today.split('-').reduce((acc, val) => acc + parseInt(val), 0);
    const questionIndex = seed % allQuestions.length;

    setChallenge(allQuestions[questionIndex]);
  };

  const handleAnswer = (answer) => {
    if (showResult) return;

    setSelectedAnswer(answer);
    setShowResult(true);

    const isCorrect = answer === challenge.answer;
    const today = getTodayString();

    // Update stats
    const stats = JSON.parse(localStorage.getItem('dailyChallengeStats') || '{}');
    
    // Calculate streak
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = `${yesterday.getFullYear()}-${yesterday.getMonth() + 1}-${yesterday.getDate()}`;
    
    const currentStreak = stats[yesterdayString]?.completed ? (stats[yesterdayString]?.streak || 0) + 1 : 1;

    stats[today] = {
      completed: true,
      correct: isCorrect,
      streak: isCorrect ? currentStreak : 0,
      question: challenge.question,
      timestamp: new Date().toISOString()
    };

    localStorage.setItem('dailyChallengeStats', JSON.stringify(stats));
    setDailyStats(stats);
    setHasCompleted(true);
  };

  const getCurrentStreak = () => {
    if (!dailyStats) return 0;
    const today = getTodayString();
    return dailyStats[today]?.streak || 0;
  };

  const getTotalCompleted = () => {
    if (!dailyStats) return 0;
    return Object.keys(dailyStats).length;
  };

  const getTotalCorrect = () => {
    if (!dailyStats) return 0;
    return Object.values(dailyStats).filter(day => day.correct).length;
  };

  if (!challenge) {
    return <div className="daily-challenge-loading">Loading today's challenge...</div>;
  }

  if (hasCompleted && !showResult) {
    const today = getTodayString();
    const todayStats = dailyStats[today];

    return (
      <div className="daily-challenge-completed">
        <h1>✅ Challenge Complete!</h1>
        <p className="completion-message">You've already completed today's challenge!</p>
        
        <div className="today-result">
          <div className={`result-badge ${todayStats.correct ? 'correct' : 'incorrect'}`}>
            {todayStats.correct ? '✓ Correct' : '✗ Incorrect'}
          </div>
          <p className="question-preview">{todayStats.question}</p>
        </div>

        <div className="stats-summary">
          <div className="stat-box">
            <div className="stat-value">{getCurrentStreak()}</div>
            <div className="stat-label">Day Streak 🔥</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{getTotalCompleted()}</div>
            <div className="stat-label">Days Completed</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{getTotalCorrect()}</div>
            <div className="stat-label">Correct Answers</div>
          </div>
        </div>

        <p className="comeback-message">Come back tomorrow for a new challenge! 🌟</p>
      </div>
    );
  }

  const isCorrect = selectedAnswer === challenge.answer;

  return (
    <div className="daily-challenge">
      <div className="challenge-header">
        <h1>🌟 Daily Challenge</h1>
        <p className="challenge-date">{new Date().toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })}</p>
      </div>

      <div className="streak-display">
        <span className="streak-icon">🔥</span>
        <span className="streak-count">{getCurrentStreak()} Day Streak</span>
      </div>

      <div className="challenge-card">
        <div className="challenge-badges">
          <span className="subject-badge">{challenge.subject}</span>
          <span className="topic-badge">{challenge.topic}</span>
          <span className="difficulty-badge">Daily Challenge</span>
        </div>

        <div className="challenge-question">
          <p>{challenge.question}</p>
        </div>

        <div className="challenge-options">
          {challenge.options.map((option, idx) => (
            <button
              key={idx}
              onClick={() => handleAnswer(option)}
              disabled={showResult}
              className={`challenge-option ${
                showResult && option === challenge.answer ? 'correct' : ''
              } ${
                showResult && selectedAnswer === option && option !== challenge.answer ? 'incorrect' : ''
              }`}
            >
              <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>

        {showResult && (
          <div className={`challenge-result ${isCorrect ? 'result-correct' : 'result-incorrect'}`}>
            <h2>{isCorrect ? '🎉 Correct!' : '😔 Incorrect'}</h2>
            
            {isCorrect ? (
              <div>
                <p className="streak-message">
                  {getCurrentStreak() > 1 ? `${getCurrentStreak()} day streak! Keep it going! 🔥` : 'Great start! Come back tomorrow!'}
                </p>
              </div>
            ) : (
              <div>
                <p><strong>Correct answer:</strong> {challenge.answer}</p>
                {challenge.explanation && (
                  <p className="explanation"><strong>Explanation:</strong> {challenge.explanation}</p>
                )}
              </div>
            )}

            <div className="challenge-stats">
              <div className="stat-item">
                <span className="stat-number">{getTotalCompleted()}</span>
                <span className="stat-text">Days Completed</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{getTotalCorrect()}</span>
                <span className="stat-text">Correct</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {getTotalCompleted() > 0 ? Math.round((getTotalCorrect() / getTotalCompleted()) * 100) : 0}%
                </span>
                <span className="stat-text">Accuracy</span>
              </div>
            </div>

            <p className="next-challenge">See you tomorrow for the next challenge! 🌟</p>
          </div>
        )}
      </div>

      {!showResult && (
        <div className="challenge-tip">
          <p>💡 <strong>Tip:</strong> Take your time - you only get one shot per day!</p>
        </div>
      )}
    </div>
  );
};

export default DailyChallenge;
